import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/users/users.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { NocConfigurationService } from '../noc-configuration.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import * as moment from 'moment';

@Component({
	selector: 'app-noc-configuration-recipients',
	templateUrl: './noc-configuration-recipients.component.html',
	styleUrls: ['./noc-configuration-recipients.component.css']
})
export class NocConfigurationRecipientsComponent implements OnInit, AfterViewInit {
	@ViewChild("addEmailModal") addEmailModal: any;
	@ViewChild('deleteRecipientModal') deleteRecipientModal: ModalComponent;

	recipients: any[] = [];
	tableData: TableData[] = [];
	newEmail: string = '';
	recipientToDeleteId: number = null;

	tableConfig: TableConfig = {
		hasExport: false,
		hasPagination: true,
		pageSize: 20,
		fileName: this.translateService.instant('noc_config.recipients')
	};

	columnConfig: ColumnsConfig[] = [
		{
			key: 'email',
			name: this.translateService.instant('g.email'),
			type: 'string',
			hasSort: true,
			hasFilter: false,
		},
		{
			key: 'delete',
			name: this.translateService.instant('g.delete'),
			type: 'icon',
			hasSort: false,
			hasFilter: false
		}
	];

	constructor(
		private translateService: TranslateService,
		private usersService: UsersService,
		private router: Router,
		private nocConfigService: NocConfigurationService,
		private sideMenuService: SideMenuService,
		private notificationMessage: NotificationMessageService
	) {}

	ngOnInit() {
		if (!this.usersService.hasAccessPermission(null, 'noc'))
			this.router.navigate(['/unauthorized']);

		this.sideMenuService.hide();

		this.loadRecipients();
	}

	ngAfterViewInit() {
		this.addEmailModal.onClose.subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.confirmAddRecipient();
			} else {
				this.newEmail = '';
			}
		});

		this.deleteRecipientModal.onClose.subscribe((confirmed) => {
			if (confirmed) {
				this.confirmDeleteRecipient();
			} else {
				this.recipientToDeleteId = null;
			}
		});
	}

	loadRecipients() {
		this.nocConfigService.getEmailRecipients().subscribe((response: any) => {
			if (response?.length) {
				this.recipients = response || [];
				this.updateTableData();
			}
		});
	}

	updateTableData() {
		this.tableData = this.recipients.map(recipient => ({
			record: recipient,
			email: { value: recipient.email },
			delete: {
				icon: 'trash',
				iconColor: '#dc3545',
				action: () => this.deleteRecipient(recipient.id)
			}
		}));
	}

	openAddDialog() {
		this.newEmail = '';
		this.addEmailModal.show();
	}

	private validateEmail(email: string): boolean {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email) && email.length <= 255;
	}

	private confirmAddRecipient() {
		const email = this.newEmail.trim();

		if (!this.validateEmail(email)) {
			this.notificationMessage.setMessage('login_register.invalid_email_address', { clearOnXTimeNavigate: 1 });
			return;
		}

		// Validation passed, add the recipient
		this.addRecipient(email);
	}

	private addRecipient(email: string) {
		this.nocConfigService.saveEmailRecipient({ email }).subscribe((response: any) => {
			if (response === 1) {
				this.notificationMessage.setMessage('g.saved_successfully', { type: 'success', clearOnXTimeNavigate: 1 });
				this.newEmail = '';
				this.loadRecipients();
			} else if (response === 2) {
				this.notificationMessage.setMessage('noc_config.email_already_exists', { clearOnXTimeNavigate: 1 });
			} else {
				this.notificationMessage.setMessage('g.failed', { clearOnXTimeNavigate: 1 });
			}
		});
	}

	deleteRecipient(id: number) {
		this.recipientToDeleteId = id;
		this.deleteRecipientModal.show();
	}

	private confirmDeleteRecipient() {
		if (this.recipientToDeleteId) {
			this.nocConfigService.deleteEmailRecipient(this.recipientToDeleteId).subscribe((response: any) => {
				if (response === 1) {
					this.notificationMessage.setMessage('g.deleted_successfully', { type: 'success', clearOnXTimeNavigate: 1 });
					this.loadRecipients();
				} else {
					this.notificationMessage.setMessage('g.failed', { clearOnXTimeNavigate: 1 });
				}
				this.recipientToDeleteId = null;
			});
		}
	}

	goBack() {
		this.router.navigate(['/noc-tools/noc-configurations']);
	}
}
